import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import queryClient from "client";
import toast from "react-hot-toast";
import axiosInstance from "utils/axiosInstance";
import { currentVersion } from "utils/constants";

interface LoginCredentials {
  phone: string;
  password: string;
  countryCode: string;
}

interface UserState {
  token: string | null;
  role: string | null;
  isFirst: boolean;
  profileComplete: boolean;
  loading: boolean;
  error: string | null;
}

interface AppState {
  userData: any;
  userLoading: boolean;
  authUser: UserState;
}

const initialState: AppState = {
  userData: {},
  userLoading: true,
  authUser: {
    token: localStorage.getItem("token") || null,
    role: localStorage.getItem("role") || null,
    isFirst: false,
    profileComplete: false,
    loading: false,
    error: null,
  },
};

export const loginUser = createAsyncThunk(
  "user/login",
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const { phone, password, countryCode } = credentials;

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/login/`,
        {
          phone,
          password,
          countryCode,
        },
      );

      // Set up axios interceptors
      axiosInstance.interceptors.request.use((config) => {
        config.headers.Authorization = `Token ${response?.data?.token}`;
        config.headers[`x-gridkey-user-role`] = response?.data?.userRole;
        return config;
      });

      // Store data in localStorage
      localStorage.setItem("token", JSON.stringify(response?.data?.token));
      localStorage.setItem("role", response?.data?.userRole);
      localStorage.setItem("product-version", currentVersion);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Login failed");
    }
  },
);

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    USER_LOADING: (state, action) => {
      state.userLoading = action.payload;
    },
    CURRENT_USER: (state, action) => {
      state.userData = action.payload;
    },
    logout: (state) => {
      state.authUser.token = null;
      state.authUser.role = null;
      localStorage.removeItem(`token`);
      localStorage.removeItem(`product-version`);
      localStorage.removeItem("role");
      localStorage.removeItem("columns");
      localStorage.removeItem("columns-order");
      queryClient.clear();
      window.location.reload();
    },
    clearError: (state) => {
      state.authUser.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.authUser.loading = true;
        state.authUser.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.authUser.loading = false;
        state.authUser.token = action.payload.token;
        state.authUser.role = action.payload.userRole;
        state.authUser.isFirst = action.payload.isFirst;
        state.authUser.profileComplete =
          action.payload.profileDetails?.profileComplete;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.authUser.loading = false;
        state.authUser.error = action.payload as string;
        toast.error(
          (action.payload as string) ||
            "Something went wrong, please try again.",
        );
      });
  },
});

export const { USER_LOADING, CURRENT_USER, logout, clearError } =
  appSlice.actions;

export default appSlice.reducer;
